import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import styled from 'styled-components'

import DropdownSelect from 'features/DropdownSelect/DropdownSelect'
import { AllIcons } from 'helpers/allIcons'
import { getCountries } from 'services/endpoints/country'
import { useUserProfile } from 'store'
import { type Country } from 'types/api/country'

import {
    SectionTitle,
    StepHeader,
    StepNumber
} from '../../../../components-ref/templates/verification/VerificationPersonalInformation/VerificationPersonalInformation'

interface VerificationCountryFormProps {
    selectedCountry: Country | null
    setSelectedCountry: (country: Country) => void
}

const DEFAULT_COUNTRY = {
    name: 'United States',
    code: 'US'
} as Country

export function VerificationCountryForm({
    selectedCountry,
    setSelectedCountry
}: VerificationCountryFormProps) {
    const [searchTerm, setSearchTerm] = useState<string>('')

    const userData = useUserProfile()
    const { t } = useTranslation()

    const { data } = useQuery('allCountries', getCountries, {
        refetchOnWindowFocus: false
    })

    /*
     * Set an immediate fallback so the parent already has US selected
     * even before the countries request finishes.
     */
    useEffect(() => {
        if (!selectedCountry) {
            setSelectedCountry(DEFAULT_COUNTRY)
        }
    }, [selectedCountry, setSelectedCountry])

    /*
     * Once the real country data arrives, replace the temporary
     * fallback with the actual backend country object.
     */
    useEffect(() => {
        if (!data) return

        // Prefer the user's saved country.
        if (userData?.country) {
            const match = data.find(
                (country: Country) => country.code === userData.country
            )

            if (match) {
                setSelectedCountry(match)
                return
            }
        }

        // Otherwise replace the temporary US object with the real one.
        const us = data.find(
            (country: Country) => country.code === 'US'
        )

        if (us) {
            setSelectedCountry(us)
        }
    }, [userData?.country, data, setSelectedCountry])

    const configData = () => {
        if (!data) {
            return []
        }

        if (searchTerm.length === 0) {
            return data
        }

        return data.filter((item: Country) =>
            item.name
                .toLowerCase()
                .includes(searchTerm.toLocaleLowerCase())
        )
    }

    return (
        <div>
            <StepHeader>
                <StepNumber>
                    Step 2
                </StepNumber>

                <SectionTitle>
                    {t('countryOfLegalResidence')}
                </SectionTitle>
            </StepHeader>

            <CountryDropdownWrapper>
                <DropdownSelect
                    customClass='verification-country-dropdown'
                    icon={AllIcons.globe_black}
                    placeHolder={t('selectCountry')}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    elementsArr={configData()}
                    topSectionSelected={
                        selectedCountry ?? DEFAULT_COUNTRY
                    }
                    topSectionSelect={setSelectedCountry}
                    hasBottomSection={false}
                />
            </CountryDropdownWrapper>
        </div>
    )
}

export const Title = styled.h3`
    font-family: 'Montserrat', sans-serif;
    font-style: normal;
    font-weight: 600;
    letter-spacing: 0.02em;
    font-size: 2.2rem;
    line-height: 2.7rem;
    color: #0d1444;
    text-align: center;
    letter-spacing: 0.04em;
    margin-bottom: 3rem;
`

const CountryDropdownWrapper = styled.div`
    padding-top: 2rem;
`